import { Dispatch, memo, useCallback, useMemo } from "react";
import { ApplicationFlowStatePaths } from "src/apps/AppFlow/screens/root";
import AssetPages from "src/apps/AppFlow/screens/root/Layout/sections/Assets";
import DebtPages from "src/apps/AppFlow/screens/root/Layout/sections/Debts";
import OtherOwnedPropertiesPages from "src/apps/AppFlow/screens/root/Layout/sections/OtherOwnedProperties";
import useSubmissionReviewPages from "src/apps/AppFlow/screens/root/Layout/sections/SubmissionReview";
import { FlowLayout, FlowLayoutSubpages, PageFooter } from "src/components";
import { NavSection } from "src/components/FlowNavigationBar/useFlowNavScroll";
import { useFlowLayoutState, useMemoizer, useMobileHeightAdjustment } from "src/hooks";
import { useResettableFlowLayoutState } from "src/hooks/useFlowLayoutState";
import { PageState } from "src/hooks/usePageState";
import { Extractor, ExtractorPath, PageStateAction } from "src/hooks/useReducerPageState";
import { ItemControl, useSubpages } from "src/hooks/useSubpages";
import { createStyles } from "src/styles";
import { filterSharedItemsByApplicant } from "src/transformers";
import {
  Applicant,
  Asset,
  Debt,
  IncomeSource,
  MortgageInformation,
  OtherOwnedProperty,
  Property,
  Term,
} from "src/types/Applicant";
import { PropertyTypeValue, UseOfFundsValue } from "src/types/Application";

import { enums, formatters, types } from "@fraction/shared";

import ApplicantInformationPages, {
  ApplicantInformationSectionArguments,
  SharedItemsArguments,
} from "./sections/ApplicantInformation";
import { IDVerificationPagesArguments, useIdentityVerificationPages } from "./sections/IdentityVerification";
import IncomeSourcePages from "./sections/IncomeSourcePages";
import MortgageInformationPages, {
  MortgageInformationSectionArguments,
} from "./sections/MortgageInformation";
import PropertyAndLoanInformationPages, {
  PropertyAndLoanInformationSectionArguments,
} from "./sections/PropertyAndLoanInformation";

const styles = createStyles({
  rootContainer: {
    position: "relative",
    overflowY: "hidden",
  },
  container: {
    width: "100%",
  },
  shortened: {
    height: "100vh",
    overflowY: "hidden",
  },
  subpageContainer: {
    position: "absolute",
    height: "100%",
    width: "100%",
    top: 0,
    bottom: 0,
    overflowY: "hidden",
  },
});

interface NavButtonHandlers {
  onNext: () => void;
  onBack: () => void;
}

export interface ReviewSection {
  applicationReview: NavButtonHandlers;
  importantTermsAndInfo: NavButtonHandlers;
}

export interface ApplicationFlowLayoutProps {
  onClose?: () => void;
  onCloseSubpage: (path: ExtractorPath<ApplicationFlowState>) => void;

  extractor: Extractor<ApplicationFlowState>;
  state: ApplicationFlowState;
  dispatch: Dispatch<PageStateAction<ApplicationFlowState>>;
  idVerificationQuestions: IDVerificationPagesArguments;
  applicantInformationSections: ApplicantInformationSectionArguments[];
  primaryPropertyMortgageSections: MortgageInformationSectionArguments[];
  propertyAndLoanInformationSection: PropertyAndLoanInformationSectionArguments;
  reviewSection: ReviewSection;
  sharedItems: SharedItemsArguments;
  navSections?: Omit<NavSection, "pageIndex">[];
  continueFlow?: boolean;
}

export interface IdVerificationQuestion extends types.KiqQuestion {
  answerNumber: number;
}

export interface IdVerificationQuestions {
  creditBureau?: enums.CreditBureau;
  questions?: IdVerificationQuestion[];
  decision?: enums.IdVerificationDecision;
  attemptId?: string;
}

export interface ApplicationFlowState {
  id?: string;
  term?: Term;
  propertyLocation?: Property;
  propertyValue?: string;
  amountRequested?: number;
  creditScoreGuess?: enums.CreditScoreGuess;
  propertyType?: PropertyTypeValue;
  useOfFunds?: UseOfFundsValue;
  applicants?: Applicant[];

  primaryPropertyMortgages?: MortgageInformation[];
  assets?: ItemControl<Asset>;
  debts?: ItemControl<Debt>;
  otherOwnedProperties?: ItemControl<OtherOwnedProperty>;
  incomeSources?: ItemControl<IncomeSource>;
  applicationReview?: null; // review page does not have any form inputs
  idVerificationQuestions?: IdVerificationQuestions;

  isComplete?: boolean;
}

const ApplicationFlowLayout = ({
  propertyAndLoanInformationSection,
  applicantInformationSections,
  primaryPropertyMortgageSections,
  reviewSection,
  sharedItems,
  extractor,
  state,
  dispatch,
  onCloseSubpage,
  navSections,
  continueFlow,
}: ApplicationFlowLayoutProps) => {
  const mainFlowState = useFlowLayoutState();
  const [subpageExternalState, resetSubpageStates] = useResettableFlowLayoutState();

  const applicants = useMemo(
    () => propertyAndLoanInformationSection.applicants?.value || [],
    [propertyAndLoanInformationSection.applicants?.value]
  );

  const propertyLocation = propertyAndLoanInformationSection.propertyLocation.value;

  const getCurrentApplicantFromBasePropertyPath = useCallback(
    (basePropertyPath: ApplicationFlowStatePaths): Applicant => {
      const baseProperty = basePropertyPath[0];
      const editContext = extractor([
        baseProperty,
        "__editContext",
      ] as ApplicationFlowStatePaths) as PageState<any>;
      return editContext?.value?.applicant;
    },
    [extractor]
  );

  const getOtherApplicantsFromBasePropertyPath = useCallback(
    (basePropertyPath: ApplicationFlowStatePaths): Applicant[] => {
      const currentApplicant = getCurrentApplicantFromBasePropertyPath(basePropertyPath);
      if (!currentApplicant || !applicants) {
        return [];
      }
      return applicants.filter(({ email }) => email?.email !== currentApplicant?.email?.email);
    },
    [applicants, getCurrentApplicantFromBasePropertyPath]
  );

  const getSharedInfo = useCallback(
    (basePropertyPath: ApplicationFlowStatePaths) => ({
      applicant: getCurrentApplicantFromBasePropertyPath(basePropertyPath),
      otherApplicants: getOtherApplicantsFromBasePropertyPath(basePropertyPath),
      applicationPropertyLocation: propertyLocation,
    }),
    [getOtherApplicantsFromBasePropertyPath, getCurrentApplicantFromBasePropertyPath, propertyLocation]
  );

  const renderers = useMemo(
    () => ({
      otherOwnedProperties: {
        PageRenderer: OtherOwnedPropertiesPages,
        properties: [
          "propertyLocation",
          "owners",
          "propertyValue",
          "propertyType",
          "rentalIncome",
          "planToSellDuringProcess",
          "mortgages",
        ],
        middleware: getSharedInfo,
      },
      assets: {
        PageRenderer: AssetPages,
        properties: ["assetDetails", "owners"],
        middleware: getSharedInfo,
      },
      debts: {
        PageRenderer: DebtPages,
        properties: ["debtTypeAndLenderName", "debtBalanceAndPayments", "owners"],
        middleware: getSharedInfo,
      },
      incomeSources: {
        PageRenderer: IncomeSourcePages,
        properties: ["incomeSourceType", "employerDetails", "incomeDetails", "owners"],
        middleware: getSharedInfo,
      },
    }),
    [getSharedInfo]
  );

  const { subpages, onCloseSubpages, onCompleteSubpages, basePropertyPath } = useSubpages({
    state,
    extractor,
    // @ts-ignore - todo - find out why this doesn't work,
    // but including it directly (without a variable declaration) does
    renderers,
  });

  const propertyAndLoanInformationPages = PropertyAndLoanInformationPages(propertyAndLoanInformationSection);

  const existingPrimaryPropertyMortgagePages = primaryPropertyMortgageSections
    .map((mortgageInfo, index) =>
      MortgageInformationPages({
        ...mortgageInfo,
        index,
        showBalanceAndLenderPage: false,
        propertyAddress: propertyAndLoanInformationSection?.propertyLocation?.value
          ? formatters.property.shortFormattedAddress(
              propertyAndLoanInformationSection?.propertyLocation?.value
            )
          : undefined,
      })
    )
    .flat();

  const otherOwnedPropertiesMemoizer = useMemoizer();
  const assetsMemoizer = useMemoizer();
  const debtsMemoizer = useMemoizer();
  const incomeSourcesMemoizer = useMemoizer();

  const idVerificationPages = useIdentityVerificationPages({ state, dispatch, extract: extractor });

  const applicantInformationPages = useMemo(
    () =>
      applicantInformationSections
        .map((applicantInfo, index) =>
          ApplicantInformationPages(
            applicantInfo,
            {
              ...sharedItems,
              otherOwnedProperties: filterSharedItemsByApplicant(
                sharedItems.otherOwnedProperties,
                applicantInfo?.applicant,
                otherOwnedPropertiesMemoizer
              ),
              assets: filterSharedItemsByApplicant(
                sharedItems.assets,
                applicantInfo?.applicant,
                assetsMemoizer
              ),
              debts: filterSharedItemsByApplicant(sharedItems.debts, applicantInfo?.applicant, debtsMemoizer),
              incomeSources: filterSharedItemsByApplicant(
                sharedItems.incomeSources,
                applicantInfo?.applicant,
                incomeSourcesMemoizer
              ),
            },
            {
              extractor,
              state,
              basePropertyPath: basePropertyPath || [],
            },
            index === 0 ? idVerificationPages : [] // only show id verification for the primary borrower
          )
        )
        .flat(),
    [
      applicantInformationSections,
      sharedItems,
      extractor,
      state,
      basePropertyPath,
      otherOwnedPropertiesMemoizer,
      assetsMemoizer,
      debtsMemoizer,
      incomeSourcesMemoizer,
      idVerificationPages,
    ]
  );

  const submissionReviewPages = useSubmissionReviewPages({ state, ...reviewSection.applicationReview });

  const pages = useMemo(
    () => [
      ...propertyAndLoanInformationPages,
      ...existingPrimaryPropertyMortgagePages,
      ...applicantInformationPages,
      ...submissionReviewPages,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      propertyAndLoanInformationSection,
      primaryPropertyMortgageSections,
      applicantInformationSections,
      submissionReviewPages,
      sharedItems,
    ]
  );

  const handleCloseSubpages = useCallback(() => {
    resetSubpageStates();
    onCloseSubpage((subpages?.map((page) => page.name) || []) as ExtractorPath<ApplicationFlowState>);
    onCloseSubpages?.();
  }, [onCloseSubpage, onCloseSubpages, resetSubpageStates, subpages]);

  const handleCompleteSubpages = useCallback(() => {
    resetSubpageStates();
    onCompleteSubpages?.();
  }, [onCompleteSubpages, resetSubpageStates]);

  const height = useMobileHeightAdjustment();

  const navSectionsWithPageIndex = useMemo(
    () =>
      (navSections || []).map((navSection) => {
        const pageIndex = pages.findIndex(({ name }) => navSection.pageId === name);

        return { ...navSection, pageIndex };
      }, []),
    [navSections, pages]
  );

  const pagesWithHeaders = useMemo(
    () =>
      pages.map((page, idx) => {
        const headerText = navSectionsWithPageIndex.reduce((acc, { pageIndex, sectionHeader }) => {
          if (idx >= pageIndex) {
            return typeof sectionHeader === "string" ? sectionHeader : "";
          }
          return acc;
        }, "");

        return { ...page, headerText };
      }, []),
    [pages, navSectionsWithPageIndex]
  );

  return (
    <div css={[styles.rootContainer, { height }]}>
      <div css={styles.container}>
        <div css={[styles.shortened, { height }]}>
          <FlowLayout
            continueFlow={continueFlow}
            externalState={mainFlowState}
            pages={pagesWithHeaders}
            navSections={navSectionsWithPageIndex}
          >
            <PageFooter />
          </FlowLayout>
        </div>
        <FlowLayoutSubpages
          subpages={subpages}
          onCloseSubpages={handleCloseSubpages}
          onCompleteSubpages={handleCompleteSubpages}
          externalState={subpageExternalState}
        />
      </div>
    </div>
  );
};

export default memo(ApplicationFlowLayout);
